import React from "react";
import { useNavigate } from "react-router-dom";

const GROUP_STYLES = {
  GREEN:  { dot: "bg-emerald-500", ring: "ring-emerald-500", chip: "bg-emerald-100 text-emerald-800 border-emerald-300", solid: "bg-emerald-600 text-white", letter: "G" },
  YELLOW: { dot: "bg-amber-500",   ring: "ring-amber-500",   chip: "bg-amber-100 text-amber-800 border-amber-300",     solid: "bg-amber-500 text-white",   letter: "Y" },
  RED:    { dot: "bg-rose-600",    ring: "ring-rose-600",    chip: "bg-rose-100 text-rose-800 border-rose-300",         solid: "bg-rose-600 text-white",    letter: "R" },
};

export const GROUP_STYLE = GROUP_STYLES;

// Quality Badge thresholds
// PREMIUM: Σ ≥ +5 AND RED ≤ 1   (empirically ~90% historically survive with R≤1, top-consensus combos)
// SOLID:   Σ ≥ 0  AND RED ≤ 1
// CAUTION: RED ≥ 2 OR Σ < 0
export const QUALITY_STYLE = {
  PREMIUM: { bg: "bg-emerald-600",  text: "text-white", chip: "bg-emerald-100 text-emerald-800 border-emerald-300", rank: 3, icon: "🏆", label: "Premium" },
  SOLID:   { bg: "bg-blue-500",     text: "text-white", chip: "bg-blue-100 text-blue-800 border-blue-300",           rank: 2, icon: "✓",  label: "Solid" },
  CAUTION: { bg: "bg-rose-500",     text: "text-white", chip: "bg-rose-100 text-rose-800 border-rose-300",           rank: 1, icon: "⚠",  label: "Caution" },
};

/** Compute the ticket's GYR mix, Σ score, and Quality tier from a ball list. */
export function computeTicketQuality(balls, consensus) {
  const mix = { GREEN: 0, YELLOW: 0, RED: 0 };
  let score = 0;
  for (const n of balls || []) {
    mix[consensus.groupOf(n)] += 1;
    score += consensus.scoreOf(n);
  }
  let tier;
  if (mix.RED >= 2 || score < 0) tier = "CAUTION";
  else if (score >= 5 && mix.RED <= 1) tier = "PREMIUM";
  else tier = "SOLID";
  return { mix, score, tier, rank: QUALITY_STYLE[tier].rank, boost: mix.GREEN * 2 + mix.YELLOW * 0.5 - mix.RED * 1 };
}

/** Apply client-side consensus filter + sort to an array of combos.
 * Each combo must have `balls`; `ls` optional. Returns an array enriched with `_quality`.
 * Modes: OFF | MAX_1_RED | NO_RED | ONLY_GREEN | RERANK
 * Sort preference: always Premium → Solid → Caution, then by original order / boost.
 */
export function applyConsensusFilter(combos, mode, consensus, { keepRankField = "rank" } = {}) {
  const decorated = (combos || []).map((c, i) => ({ ...c, _quality: computeTicketQuality(c.balls || c.numbers || [], consensus), _origIdx: i }));
  let list = decorated;
  if (mode === "MAX_1_RED") list = decorated.filter(c => c._quality.mix.RED <= 1);
  else if (mode === "NO_RED") list = decorated.filter(c => c._quality.mix.RED === 0);
  else if (mode === "ONLY_GREEN") list = decorated.filter(c => c._quality.mix.YELLOW === 0 && c._quality.mix.RED === 0);
  // Always sort by quality tier (Premium > Solid > Caution), then by original order
  list = [...list].sort((a, b) => {
    if (a._quality.rank !== b._quality.rank) return b._quality.rank - a._quality.rank;
    if (mode === "RERANK") return b._quality.boost - a._quality.boost;
    return a._origIdx - b._origIdx;
  });
  return list;
}

/** Compact Quality Badge — use next to any ticket. */
export function QualityBadge({ tier, score, size = "sm" }) {
  if (!tier) return null;
  const s = QUALITY_STYLE[tier];
  if (!s) return null;
  const px = size === "xs" ? "text-[9px] px-1.5 py-0.5" : size === "sm" ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-bold ${s.bg} ${s.text} ${px}`} title={`Quality: ${s.label}${score !== undefined ? ` · Σ ${score >= 0 ? '+' : ''}${score}` : ''}`}>
      <span>{s.icon}</span>
      <span className="uppercase tracking-wide">{s.label}</span>
      {score !== undefined && <span className="font-mono">{score >= 0 ? `+${score}` : score}</span>}
    </span>
  );
}

/** Small circular dot showing the consensus group for a number. */
export function GroupDot({ group, size = 6, score }) {
  const s = GROUP_STYLES[group] || GROUP_STYLES.RED;
  const px = `w-${size} h-${size}`;
  const title = score !== undefined ? `${group} · Consensus ${score >= 0 ? '+' : ''}${score}` : group;
  return <span className={`inline-block ${px} rounded-full ${s.dot}`} title={title} />;
}

/** Letter badge (G/Y/R). */
export function GroupLetter({ group, score, showScore = false, size = "sm" }) {
  const s = GROUP_STYLES[group] || GROUP_STYLES.RED;
  const base = size === "xs"
    ? "text-[9px] px-1 py-0 rounded"
    : size === "sm"
    ? "text-[10px] px-1.5 py-0.5 rounded"
    : "text-xs px-2 py-0.5 rounded";
  return (
    <span className={`inline-flex items-center gap-0.5 font-bold ${base} ${s.chip}`} title={`Consensus ${group}`}>
      {s.letter}
      {showScore && score !== undefined && <span className="font-mono">{score >= 0 ? `+${score}` : score}</span>}
    </span>
  );
}

/** Rings-around a number ball to color-code by consensus group. */
export function ConsensusRing({ group, children, className = "" }) {
  const s = GROUP_STYLES[group] || GROUP_STYLES.RED;
  return (
    <span className={`inline-flex items-center justify-center rounded-full ring-2 ring-offset-1 ${s.ring} ${className}`}>
      {children}
    </span>
  );
}

/** Small "🔗 Refer to: Number Tier Consensus" link. */
export function ConsensusRefLink({ className = "" }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate("/number-tier-consensus")}
      className={`inline-flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline font-bold ${className}`}
      data-testid="consensus-ref-link"
      title="Open Number Tier Consensus"
    >
      🔗 Refer to: Number Tier Consensus
    </button>
  );
}

/** Compact legend chip showing G/Y/R counts. */
export function GroupLegend({ groups }) {
  return (
    <div className="inline-flex items-center gap-2 text-[11px]">
      <span className="text-gray-500 font-bold uppercase tracking-wider text-[10px]">Consensus:</span>
      {["GREEN", "YELLOW", "RED"].map(g => {
        const s = GROUP_STYLES[g];
        return (
          <span key={g} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${s.chip}`}>
            <span className={`inline-block w-2 h-2 rounded-full ${s.dot}`} />
            <span className="font-bold">{g[0]}</span>
            <span className="font-mono">{groups?.[g]?.length ?? 0}</span>
          </span>
        );
      })}
    </div>
  );
}
